<template>
  <div class='assets-column-headers' :class='tab' v-if='display'>
    <div
      class='header'
      v-for='(header, index) in formattedHeaders'
      :key='index'
      :class="[{ 'clickable': clickable }, `item_${index}`]"
      @click='headerClicked(header)'
    >
      {{ $t(`${ header }`) }}
      <span v-if='clickable && lastHeaderClicked === header' class='material-icons-outlined direction'>
        {{ directionIcon }}
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AssetsColumnHeaders',
  props: {
    tab: {
      default: 'infra',
      type: String,
    },
    headers: {
      required: true,
      default: () => [],
      type: Array,
    },
    display: {
      default: false,
      type: Boolean,
    },
    clickable: {
      type: Boolean,
      default: false,
      required: false,
    },
    sortDirection: {
      type: String,
      required: false,
      default: 'keyboard_arrow_up',
    },
    sortOption: {
      type: String,
      required: false,
      default: '',
    },
    clickedHeaderValue: {
      required: false,
      default: () => {},
      type: Object,
    },
  },
  data() {
    return {
      itemClicksCounter: {},
      lastHeaderClicked: this.sortOption || this.headers[0] || '',
      directionIcon: this.sortDirection || 'keyboard_arrow_up',
    };
  },
  methods: {
    headerClicked(header) {
      if (this.clickable) {
        this.lastHeaderClicked = header;
        if (!Object.keys(this.itemClicksCounter).includes(header)) {
          this.itemClicksCounter[header] = 0;
        }
        this.itemClicksCounter[header] += 1;
        if (this.clickedHeaderValue) {
          if (Object.keys(this.clickedHeaderValue).includes(header)) {
            if (this.clickedHeaderValue[header] === 2) {
              this.itemClicksCounter[header] = 0;
            }
            this.itemClicksCounter[header] += 1;
          }
        }
        this.directionIcon = this.itemClicksCounter[header] % 2 === 0 ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
        this.$emit('headerClicked', { header, clicks: this.itemClicksCounter[header] });
      }
    },
  },
  computed: {
    formattedHeaders() {
      return ['', ...this.headers, ''];
    },
  },
};
</script>

<style lang='scss' scoped>
@import '../../assets/styles/variables.scss';
.assets-column-headers {
  display: flex;
  padding: 12px 0;
  .header {
    display: flex;
    align-items: center;
    font-family: $default-application-font-regular;
    font-size: 12px;
    letter-spacing: 1.1px;
    line-height: 15px;
    color: var(--text-secondary);
    text-transform: uppercase;
    &.item_1 {
      width: 368px;
    }
    &.item_2 {
      width: 116px;
    }
    &.item_3 {
      width: 107px;
    }
    &.item_4 {
      width: 162px;
    }
    &.item_5 {
      width: 90px;
    }
    &.item_6 {
      width: 150px;
    }
    &.item_7 {
      width: 148px;
    }
    &.item_8 {
      width: 127px;
    }
    &.item_9 {
      width: 118px;
    }
    &.item_10 {
      width: 247px;
    }
    &.clickable {
      &:hover {
        cursor: pointer;
      }
    }
    .header {
      font-size: 14px;
    }
  }
  &.topLevelDomain {
    .header {
      &.item_0 {
        width: 1.7%;
      }
      &.item_1 {
        width: 24.1%;
      }
      &.item_2 {
        width: 7.1%;
      }
      &.item_3 {
        width: 7.2%;
      }
      &.item_4 {
        width: 8.45%;
      }
      &.item_5 {
        width: 2.25%;
        pointer-events: none;
      }
      &.item_6 {
        width: 10%;
        display: flex;
        justify-content: center;
        pointer-events: none;
      }
      &.item_7 {
        width: 5.2%;
      }
      &.item_8 {
        width: 7.3%;
        pointer-events: none;
      }
      &.item_9 {
        width: 5.2%;
        pointer-events: none;
      }
      &.item_10 {
        width: 6.7%;
        pointer-events: none;
      }
      &.item_11 {
        width: 14.7%;
      }
      &.item_12 {
        width: 0%;
      }
    }
  }
  &.cloud {
    .header {
      &.item_0 {
        width: 60px;
      }
      &.item_1 {
        width: 30.5%;
      }
      &.item_2 {
        width: 23.5%;
      }
      &.item_3 {
        width: 14%;
      }
      &.item_4 {
        width: 10%;
      }
      &.item_5 {
        width: 10%;
      }
    }
  }
  &.cloudAccount {
    .header {
      &.item_0 {
        width: 2.1%;
      }
      &.item_1 {
        width: 38.2%;
      }
    }
  }
  &.services {
    .header {
      &.item_0 {
        width: 3.4%;
      }
      &.item_1 {
        width: 9.4%;
      }
      &.item_2 {
        width: 14.1%;
      }
      &.item_3 {
        width: 14.1%;
      }
      &.item_4 {
        width: 12.8%;
      }
      &.item_5 {
        width: 41%;
      }
      &.item_6 {
        width: 3.2%;
      }
    }
  }
  &.servicesDetails {
    .header {
      &.item_0 {
        width: 4%;
      }
      &.item_1 {
        width: 49%;
      }
      &.item_2 {
        width: 8.8%;
      }
      &.item_3 {
        width: 8.8%;
      }
      &.item_4 {
        width: 8.8%;
      }
      &.item_5 {
        width: 8.8%;
      }
      &.item_6 {
        width: 11.8%;
      }
    }
  }
  &.infra {
    .header {
      &.item_0 {
        width: 70px;
      }
      &.item_1 {
        width: 190px;
      }
      &.item_2 {
        width: 140px;
      }
      &.item_3 {
        width: 285px;
      }
      &.item_4 {
        width: 470px;
      }
    }
  }
  &.organizations {
    .header {
      &.item_0 {
        // width: 0.5%;
      }
      &.item_1 {
        width: 20%;
      }
      &.item_2 {
        width: 20%;
      }
      &.item_3 {
        width: 20%;
      }
      &.item_4 {
        width: 20%;
      }
    }
  }
}
</style>
